import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { CollectionsSkeleton } from './PageSkeletons';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Sheet, SheetContent } from '../ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  IndianRupee, Search, Download, Filter, Trash2, Wallet, CreditCard,
  Smartphone, Building, ChevronDown, X, Calendar, ArrowLeft, Pencil,
} from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { toast } from '../../hooks/use-toast';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import EditPaymentModal from './EditPaymentModal';
import RecordPaymentModal from './RecordPaymentModal';
import { emit, EVT } from '../../services/dataSyncBus';
import { useDataSync } from '../../hooks/useDataSync';
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus';
import { useEmptyStateTracker } from '../../hooks/useEmptyStateTracker';
import { LastSyncedBadge } from './LastSyncedBadge';
import { track } from '../../services/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

const METHOD_META = {
  cash:          { label: 'Cash',          color: 'bg-green-100 text-green-800',   icon: Wallet },
  upi:           { label: 'UPI',           color: 'bg-purple-100 text-purple-800', icon: Smartphone },
  card:          { label: 'Card',          color: 'bg-blue-100 text-blue-800',     icon: CreditCard },
  bank_transfer: { label: 'Bank Transfer', color: 'bg-orange-100 text-orange-800', icon: Building },
  cheque:        { label: 'Cheque',        color: 'bg-yellow-100 text-yellow-800', icon: CreditCard },
};

// ---------- Date helpers ----------
const fmtDate = (d) => d.toISOString().slice(0, 10);
const todayISO = () => fmtDate(new Date());

const datePresets = () => {
  const today = new Date();
  const start = new Date(today); const end = new Date(today);
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const last7 = new Date(today); last7.setDate(today.getDate() - 6);
  const last30 = new Date(today); last30.setDate(today.getDate() - 29);
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  const last3 = new Date(today); last3.setMonth(today.getMonth() - 3);
  const last6 = new Date(today); last6.setMonth(today.getMonth() - 6);
  const last12 = new Date(today); last12.setMonth(today.getMonth() - 12);
  const prevYearStart = new Date(today.getFullYear() - 1, 0, 1);
  const prevYearEnd = new Date(today.getFullYear() - 1, 11, 31);
  return [
    { key: 'all',          label: 'All Time',          start: '', end: '' },
    { key: 'today',        label: 'Today',             start: fmtDate(start), end: fmtDate(end) },
    { key: 'yesterday',    label: 'Yesterday',         start: fmtDate(yesterday), end: fmtDate(yesterday) },
    { key: 'this_month',   label: today.toLocaleDateString('en-US', { month: 'long' }), start: fmtDate(monthStart), end: fmtDate(monthEnd) },
    { key: 'last_7',       label: 'Last 7 Days',       start: fmtDate(last7), end: fmtDate(today) },
    { key: 'last_30',      label: 'Last 30 Days',      start: fmtDate(last30), end: fmtDate(today) },
    { key: 'prev_month',   label: 'Previous Month',    start: fmtDate(prevMonthStart), end: fmtDate(prevMonthEnd) },
    { key: 'last_3m',      label: 'Last 3 Months',     start: fmtDate(last3), end: fmtDate(today) },
    { key: 'last_6m',      label: 'Last 6 Months',     start: fmtDate(last6), end: fmtDate(today) },
    { key: 'last_12m',     label: 'Last 12 Months',    start: fmtDate(last12), end: fmtDate(today) },
    { key: 'prev_year',    label: 'Previous Year',     start: fmtDate(prevYearStart), end: fmtDate(prevYearEnd) },
    { key: 'custom',       label: 'Custom Date Range', start: '', end: '' },
  ];
};

// On first load default to "this month" — UNLESS the URL carries a
// ?range= hint from a Home-page KPI click, in which case we honor that.
const initialCollectedOn = (rangeParam) => {
  const presets = datePresets();
  const keyMap = { today: 'today', month: 'this_month', yesterday: 'yesterday' };
  const wanted = keyMap[rangeParam] || 'this_month';
  const p = presets.find(x => x.key === wanted) || presets.find(x => x.key === 'this_month');
  return { preset: p.key, start: p.start, end: p.end };
};

const COLLECTION_TYPES = [
  { key: 'rent',        label: 'Rent' },
  { key: 'deposit',     label: 'Security Deposit' },
  { key: 'electricity', label: 'Electricity' },
  { key: 'water',       label: 'Water' },
  { key: 'maintenance', label: 'Maintenance' },
  { key: 'food',        label: 'Food' },
  { key: 'late_fine',   label: 'Late Fine' },
  { key: 'other',       label: 'Other' },
];

const PAYMENT_MODES = [
  { key: 'cash',          label: 'Cash' },
  { key: 'upi',           label: 'UPI' },
  { key: 'card',          label: 'Card' },
  { key: 'bank_transfer', label: 'Bank Transfer' },
  { key: 'cheque',        label: 'Cheque' },
];

const TENANT_TYPES = [
  { key: 'active', label: 'Active' },
  { key: 'notice', label: 'Under Notice' },
  { key: 'exited', label: 'Exited' },
];

const FILTER_SECTIONS = [
  { key: 'collectedOn',     label: 'Was Collected On',  kind: 'date' },
  { key: 'dueOn',           label: 'Was Due On',        kind: 'date' },
  { key: 'paymentMode',     label: 'Payment Mode',      kind: 'multi', options: PAYMENT_MODES },
  { key: 'receivedBy',      label: 'Received By',       kind: 'multi', options: [] /* filled at runtime */ },
  { key: 'collectionTypes', label: 'Collection Types',  kind: 'multi', options: COLLECTION_TYPES },
  { key: 'tenantTypes',     label: 'Tenant Types',      kind: 'multi', options: TENANT_TYPES },
];

const Collections = () => {
  const { token, user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [rowDeleting, setRowDeleting] = useState(null);
  const [recordOpen, setRecordOpen] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  // ---- Filters state ----
  // Honor URL hint (e.g. ?range=today from Home KPI click) on first paint.
  const [searchParams] = useSearchParams();
  const initialRange = searchParams.get('range');
  const [filters, setFilters] = useState({
    collectedOn:     initialCollectedOn(initialRange),
    dueOn:           { preset: 'all', start: '', end: '' },
    paymentMode:     [],
    receivedBy:      [],
    collectionTypes: [],
    tenantTypes:     [],
  });
  const [sheetOpen, setSheetOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('collectedOn');

  const fetchData = useCallback(async () => {
    try {
      const [pRes, tRes] = await Promise.all([
        axios.get(`${API}/money/payments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tenants`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      setPayments(pRes.data || []);
      setTenants(tRes.data || []);
    } catch (e) {
      console.error('Error fetching collections:', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Hot refresh on any payment or tenant change anywhere in the app
  useDataSync([EVT.PAYMENT, EVT.TENANT], fetchData);
  useRefetchOnFocus(fetchData);
  useEmptyStateTracker('collections', payments.length, loading);

  // Build "received by" options from payments / current user (only one for now)
  const receivedByOptions = useMemo(() => {
    const set = new Map();
    payments.forEach(p => {
      const id = p.received_by_id || p.user_id;
      const name = p.received_by_name || user?.name || 'Me';
      if (id) set.set(id, name);
    });
    if (set.size === 0 && user) set.set(user.id, user.name || 'Me');
    return Array.from(set.entries()).map(([key, label]) => ({ key, label }));
  }, [payments, user]);

  // ---- Apply filters ----
  const tenantById = useMemo(() => {
    const m = new Map();
    (tenants || []).forEach(t => m.set(t.id, t));
    return m;
  }, [tenants]);

  const filtered = useMemo(() => {
    return (payments || []).filter(p => {
      // Search
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const blob = [
          p.tenant_name, p.room_number, p.transaction_id, p.notes, p.property_name,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!blob.includes(q)) return false;
      }
      // Date: collectedOn (payment_date)
      if (filters.collectedOn.start && p.payment_date < filters.collectedOn.start) return false;
      if (filters.collectedOn.end   && p.payment_date > filters.collectedOn.end)   return false;
      // Date: dueOn (use payment.month derived to first of month)
      if (filters.dueOn.start || filters.dueOn.end) {
        if (!p.month) return false;
        // month is "YYYY-MM" — compare as first of that month
        const monthStart = `${p.month}-01`;
        if (filters.dueOn.start && monthStart < filters.dueOn.start) return false;
        if (filters.dueOn.end   && monthStart > filters.dueOn.end)   return false;
      }
      // Multi-select: payment mode
      if (filters.paymentMode.length && !filters.paymentMode.includes(p.payment_method)) return false;
      // Received By
      if (filters.receivedBy.length) {
        const rb = p.received_by_id || p.user_id;
        if (!filters.receivedBy.includes(rb)) return false;
      }
      // Collection types
      if (filters.collectionTypes.length && !filters.collectionTypes.includes(p.payment_type)) return false;
      // Tenant types
      if (filters.tenantTypes.length) {
        const t = tenantById.get(p.tenant_id);
        const status = t?.status || 'active';
        if (!filters.tenantTypes.includes(status)) return false;
      }
      return true;
    });
  }, [payments, searchTerm, filters, tenantById]);

  const totalCollected = filtered.reduce((s, p) => s + (p.amount || 0), 0);

  // ----- KPI tiles (follow the active "Collected On" date filter) -----
  // When the user picks "This Month" (the default) the tiles read May 2026's
  // numbers. Pick "Last 3 Months" → tiles aggregate over those 3 months.
  // Pick "All" → tiles show lifetime totals.
  const kpiTiles = useMemo(() => {
    const range = filters.collectedOn;
    const inRange = (p) => {
      const d = (p.payment_date || '').slice(0, 10);
      if (!d) return false;
      if (range.preset === 'all' || (!range.start && !range.end)) return true;
      if (range.start && d < range.start) return false;
      if (range.end   && d > range.end)   return false;
      return true;
    };
    const ofType = (p, t) => p.payment_type === t;
    const sumIf = (fn) => payments.filter(fn).reduce((s, p) => s + (p.amount || 0), 0);

    // Friendly period label for the tile titles
    const periodLabel = (() => {
      if (range.preset === 'all' || (!range.start && !range.end)) return 'All-time';
      const preset = datePresets().find(x => x.key === range.preset);
      if (preset && preset.key !== 'custom') return preset.label;
      return 'Selected period';
    })();

    // For the "<Month> Dues Collection" tile we still need the current calendar
    // month key (since "dues for this month" is an absolute concept).
    const thisMonthKey = new Date().toISOString().slice(0, 7);
    const inThisCalendarMonth = (p) => (p.payment_date || '').slice(0, 7) === thisMonthKey;

    return [
      { key: 'total',       label: `${periodLabel} Collection`,             value: sumIf(p => inRange(p)) },
      { key: 'rent',        label: `${periodLabel} Rent Collection`,        value: sumIf(p => inRange(p) && ofType(p, 'rent')) },
      { key: 'deposit',     label: `${periodLabel} Deposit Collection`,     value: sumIf(p => inRange(p) && ofType(p, 'deposit')) },
      { key: 'electricity', label: `${periodLabel} Electricity Collection`, value: sumIf(p => inRange(p) && ofType(p, 'electricity')) },
      { key: 'food',        label: `${periodLabel} Food Collection`,        value: sumIf(p => inRange(p) && ofType(p, 'food')) },
      { key: 'late_fine',   label: `${periodLabel} Late Fine Collection`,   value: sumIf(p => inRange(p) && ofType(p, 'late_fine')) },
      // "This-month dues collection" stays absolute regardless of filter so the
      // owner always sees how much of the current month's rent has been paid.
      { key: 'month_dues',  label: `This-month dues collected`,             value: sumIf(p => inThisCalendarMonth(p) && ofType(p, 'rent') && p.month === thisMonthKey) },
      { key: 'lifetime',    label: `Lifetime Collection`,                   value: sumIf(() => true) },
    ];
  }, [payments, filters.collectedOn]);

  // ---- Filter chip helpers ----
  const collectedOnLabel = () => {
    const p = datePresets().find(x => x.key === filters.collectedOn.preset);
    if (filters.collectedOn.preset === 'custom') {
      return `${filters.collectedOn.start || '…'} → ${filters.collectedOn.end || '…'}`;
    }
    if (p?.start && p?.end) {
      const s = new Date(p.start); const e = new Date(p.end);
      const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${fmt(s)} – ${fmt(e)}`;
    }
    return p?.label || 'All Time';
  };
  const dueOnLabel = () => {
    const p = datePresets().find(x => x.key === filters.dueOn.preset);
    if (filters.dueOn.preset === 'custom') {
      return `${filters.dueOn.start || '…'} → ${filters.dueOn.end || '…'}`;
    }
    return p?.label || 'Was Due On';
  };
  const multiLabel = (selected, options, fallback) => {
    if (!selected.length) return fallback;
    if (selected.length === 1) return options.find(o => o.key === selected[0])?.label || fallback;
    return `${selected.length} selected`;
  };

  // Active filter count for "Filters" badge
  const activeFilterCount =
    (filters.collectedOn.preset !== 'all' && filters.collectedOn.preset ? 1 : 0) +
    (filters.dueOn.preset !== 'all' && filters.dueOn.preset ? 1 : 0) +
    (filters.paymentMode.length ? 1 : 0) +
    (filters.receivedBy.length ? 1 : 0) +
    (filters.collectionTypes.length ? 1 : 0) +
    (filters.tenantTypes.length ? 1 : 0);

  const clearAll = () => {
    setFilters({
      collectedOn: { preset: 'all', start: '', end: '' },
      dueOn:       { preset: 'all', start: '', end: '' },
      paymentMode: [],
      receivedBy:  [],
      collectionTypes: [],
      tenantTypes: [],
    });
    setSearchTerm('');
  };

  // ---- Bulk select / delete / export ----
  const toggleSelect = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => {
    if (selectedIds.length === filtered.length) setSelectedIds([]);
    else setSelectedIds(filtered.map(p => p.id));
  };
  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const res = await axios.post(`${API}/money/payments/bulk-delete`, selectedIds, {
        headers: { Authorization: `Bearer ${token}` },
      });
      emit(EVT.PAYMENT, { action: 'bulk_deleted', ids: selectedIds });
      emit(EVT.TENANT,  { action: 'payment_status_synced_bulk' });
      toast({ title: `Deleted ${res.data.deleted_count} payments` });
      setSelectedIds([]);
      setShowBulkDelete(false);
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setBulkDeleting(false); }
  };
  const handleRowDelete = async (p) => {
    if (!window.confirm(`Delete this ${p.payment_type || 'payment'} of ₹${Math.round(p.amount)} from ${p.tenant_name || 'tenant'}? This cannot be undone.`)) return;
    setRowDeleting(p.id);
    try {
      await axios.delete(`${API}/money/payments/${p.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      emit(EVT.PAYMENT, { action: 'deleted', id: p.id });
      emit(EVT.TENANT,  { action: 'payment_status_synced', tenant_id: p.tenant_id });
      toast({ title: 'Payment deleted', description: 'Tenant status synced.' });
      setSelectedIds(prev => prev.filter(id => id !== p.id));
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setRowDeleting(null); }
  };
  const exportToExcel = () => {
    const rows = filtered.map(p => ({
      Date: p.payment_date,
      Tenant: p.tenant_name || '',
      Room: p.room_number || '',
      Property: p.property_name || '',
      Amount: p.amount || 0,
      Type: p.payment_type || '',
      Month: p.month || '',
      Mode: p.payment_method || '',
      'Txn Ref': p.transaction_id || '',
      Status: p.status || '',
      Notes: p.notes || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Collections');
    XLSX.writeFile(wb, `collections_${todayISO()}.xlsx`);
  };

  if (loading) {
    return <CollectionsSkeleton />;
  }

  return (
    <div className="p-6 space-y-4 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Collection</h1>
          <p className="text-sm text-gray-500 mt-0.5">View and manage all payment collections</p>
        </div>
        <div className="flex items-center gap-2">
          <LastSyncedBadge syncedAt={lastSyncedAt} onRefresh={fetchData} refreshing={loading} />
          <Button
            onClick={() => { track('cta_record_payment_clicked', { surface: 'collections' }); setRecordOpen(true); }}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="record-payment-btn"
          >
            <IndianRupee className="mr-2 h-4 w-4" /> Record Payment
          </Button>
          <Button onClick={() => { track('cta_export_clicked', { surface: 'collections', rows: filtered.length }); exportToExcel(); }} variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" data-testid="export-collections-btn">
            <Download className="mr-2 h-4 w-4" /> Export
          </Button>
        </div>
      </div>

      {/* KPI strip — horizontal scroll, live from payments */}
      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory" data-testid="collections-kpi-strip">
        {kpiTiles.map(k => (
          <Card
            key={k.key}
            data-testid={`collection-kpi-${k.key}`}
            className="p-4 min-w-[150px] snap-start border border-gray-200 hover:border-green-300 transition-colors"
          >
            <p className="text-xl font-bold text-green-600 tabular-nums leading-none">
              {inr(k.value)}
            </p>
            <p className="text-[11px] text-gray-700 font-medium mt-2 leading-tight">{k.label}</p>
          </Card>
        ))}
      </div>

      {/* Bulk action bar */}
      {selectedIds.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-center justify-between" data-testid="bulk-action-bar">
          <p className="text-sm text-gray-900"><strong>{selectedIds.length}</strong> payment(s) selected</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelectedIds([])}>Clear</Button>
            <Button
              data-testid="bulk-delete-btn"
              size="sm"
              onClick={() => setShowBulkDelete(true)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 size={14} className="mr-1" /> Delete Selected
            </Button>
          </div>
        </div>
      )}

      {/* Search */}
      <Card className="p-4 border border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <Input
            placeholder="Search tenants with collections…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="collections-search"
          />
        </div>

        {/* Filter chip strip */}
        <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide" data-testid="filter-chip-strip">
          <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold whitespace-nowrap mr-1">Was Collected On</p>
          <DateChip
            testId="chip-collectedOn"
            value={filters.collectedOn}
            onChange={(v) => setFilters(f => ({ ...f, collectedOn: v }))}
            label={collectedOnLabel()}
            highlight={filters.collectedOn.preset !== 'all' && filters.collectedOn.preset !== ''}
          />

          <DateChip
            testId="chip-dueOn"
            value={filters.dueOn}
            onChange={(v) => setFilters(f => ({ ...f, dueOn: v }))}
            label={filters.dueOn.preset === 'all' || !filters.dueOn.preset ? 'Was Due On' : dueOnLabel()}
            highlight={filters.dueOn.preset !== 'all' && filters.dueOn.preset !== ''}
          />

          <MultiChip
            testId="chip-paymentMode"
            label={multiLabel(filters.paymentMode, PAYMENT_MODES, 'Payment Mode')}
            options={PAYMENT_MODES}
            selected={filters.paymentMode}
            onChange={(arr) => setFilters(f => ({ ...f, paymentMode: arr }))}
            highlight={filters.paymentMode.length > 0}
          />

          <MultiChip
            testId="chip-receivedBy"
            label={multiLabel(filters.receivedBy, receivedByOptions, 'Received By')}
            options={receivedByOptions}
            selected={filters.receivedBy}
            onChange={(arr) => setFilters(f => ({ ...f, receivedBy: arr }))}
            highlight={filters.receivedBy.length > 0}
          />

          <MultiChip
            testId="chip-collectionTypes"
            label={multiLabel(filters.collectionTypes, COLLECTION_TYPES, 'Collection Types')}
            options={COLLECTION_TYPES}
            selected={filters.collectionTypes}
            onChange={(arr) => setFilters(f => ({ ...f, collectionTypes: arr }))}
            highlight={filters.collectionTypes.length > 0}
          />

          <MultiChip
            testId="chip-tenantTypes"
            label={multiLabel(filters.tenantTypes, TENANT_TYPES, 'Tenant Types')}
            options={TENANT_TYPES}
            selected={filters.tenantTypes}
            onChange={(arr) => setFilters(f => ({ ...f, tenantTypes: arr }))}
            highlight={filters.tenantTypes.length > 0}
          />

          <button
            data-testid="open-filters-sheet"
            onClick={() => setSheetOpen(true)}
            className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100 whitespace-nowrap"
          >
            <Filter size={14} /> Filters
            {activeFilterCount > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center w-5 h-5 text-[10px] rounded-full bg-orange-600 text-white font-bold" data-testid="filter-count-badge">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </Card>

      {/* Results header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-700" data-testid="results-count">
          <span className="font-bold text-gray-900">{inr(totalCollected)}</span>
          <span className="mx-1.5 text-gray-400">/</span>
          <span className="font-medium">{filtered.length} {filtered.length === 1 ? 'Result' : 'Results'} Found</span>
        </p>
        {(activeFilterCount > 0 || searchTerm) && (
          <button onClick={clearAll} data-testid="clear-all-filters" className="text-sm font-medium text-orange-700 hover:text-orange-800 underline">
            Clear All
          </button>
        )}
      </div>

      {/* Table */}
      <Card className="overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="collections-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-3 w-10">
                  <input
                    data-testid="select-all-payments"
                    type="checkbox"
                    checked={filtered.length > 0 && selectedIds.length === filtered.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="px-3 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Tenant</th>
                <th className="px-3 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Room</th>
                <th className="px-3 py-3 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                <th className="px-3 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Received On</th>
                <th className="px-3 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Payment Mode</th>
                <th className="px-3 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                <th className="px-3 py-3 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider w-20">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center p-12 text-gray-500" data-testid="collections-empty">
                    There are no collections present.
                  </td>
                </tr>
              ) : (
                filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-orange-50/30" data-testid={`payment-row-${p.id}`}>
                    <td className="px-3 py-2.5">
                      <input
                        data-testid={`payment-select-${p.id}`}
                        type="checkbox"
                        checked={selectedIds.includes(p.id)}
                        onChange={() => toggleSelect(p.id)}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center text-[11px] font-bold">
                          {(p.tenant_name || '?').charAt(0)}
                        </div>
                        <p className="text-xs font-semibold text-gray-900 truncate max-w-[160px]">{p.tenant_name || 'Unknown'}</p>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-700">
                      {p.room_number ? `Room ${p.room_number}` : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <span className="text-sm font-bold text-green-700 tabular-nums">{inr(p.amount)}</span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-700 whitespace-nowrap">
                      {p.payment_date ? new Date(p.payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td className="px-3 py-2.5">
                      {METHOD_META[p.payment_method] ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${METHOD_META[p.payment_method].color}`}>
                          {METHOD_META[p.payment_method].label}
                        </span>
                      ) : <span className="text-xs capitalize text-gray-500">{p.payment_method || '—'}</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${
                        p.payment_type === 'rent' ? 'bg-blue-100 text-blue-800' :
                        p.payment_type === 'deposit' ? 'bg-purple-100 text-purple-800' :
                        p.payment_type === 'token' ? 'bg-amber-100 text-amber-800' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {p.payment_type || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <button
                        data-testid={`payment-edit-${p.id}`}
                        onClick={() => setEditingPayment(p)}
                        title="Edit payment"
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        data-testid={`payment-delete-${p.id}`}
                        onClick={() => handleRowDelete(p)}
                        disabled={rowDeleting === p.id}
                        title="Delete payment"
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-40"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Filter sheet (right slide-in) */}
      <CollectionFiltersSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        filters={filters}
        setFilters={setFilters}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        receivedByOptions={receivedByOptions}
        onClear={() => {
          clearAll();
        }}
      />

      <DeleteConfirmationModal
        open={showBulkDelete}
        onClose={() => setShowBulkDelete(false)}
        onConfirm={handleBulkDelete}
        title="Delete Selected Payments"
        message={`Are you sure you want to delete ${selectedIds.length} payment(s)? This action cannot be undone.`}
        itemName={`${selectedIds.length} payments`}
        loading={bulkDeleting}
      />

      <EditPaymentModal
        isOpen={!!editingPayment}
        onClose={() => setEditingPayment(null)}
        onSuccess={() => { setEditingPayment(null); fetchData(); }}
        payment={editingPayment}
        token={token}
      />

      <RecordPaymentModal
        isOpen={recordOpen}
        onClose={() => setRecordOpen(false)}
        onSuccess={() => { setRecordOpen(false); fetchData(); }}
        token={token}
      />
    </div>
  );
};

// ============================================================
// Reusable: DateChip — popover with presets + custom range
// ============================================================
const DateChip = ({ value, onChange, label, highlight, testId }) => {
  const [open, setOpen] = useState(false);
  const presets = datePresets();
  const [custom, setCustom] = useState({ start: value.start, end: value.end });

  useEffect(() => { setCustom({ start: value.start, end: value.end }); }, [value]);

  const apply = (preset) => {
    if (preset.key === 'custom') {
      onChange({ preset: 'custom', start: custom.start || '', end: custom.end || '' });
    } else {
      onChange({ preset: preset.key, start: preset.start, end: preset.end });
    }
    setOpen(false);
  };
  const applyCustom = () => {
    onChange({ preset: 'custom', start: custom.start, end: custom.end });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={testId}
          className={`inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
            highlight
              ? 'bg-orange-50 border-orange-300 text-orange-800 font-medium'
              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Calendar size={12} /> {label}
          <ChevronDown size={12} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="max-h-80 overflow-y-auto">
          {presets.map(p => (
            <button
              key={p.key}
              data-testid={`${testId}-preset-${p.key}`}
              onClick={() => apply(p)}
              className={`w-full text-left text-sm px-2.5 py-1.5 rounded hover:bg-orange-50 ${
                value.preset === p.key ? 'bg-orange-100 text-orange-800 font-medium' : 'text-gray-700'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {value.preset === 'custom' && (
          <div className="border-t mt-2 pt-2 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={custom.start}
                onChange={(e) => setCustom(c => ({ ...c, start: e.target.value }))}
                className="text-xs border border-gray-300 rounded px-2 py-1"
                data-testid={`${testId}-custom-start`}
              />
              <input
                type="date"
                value={custom.end}
                onChange={(e) => setCustom(c => ({ ...c, end: e.target.value }))}
                className="text-xs border border-gray-300 rounded px-2 py-1"
                data-testid={`${testId}-custom-end`}
              />
            </div>
            <Button size="sm" className="w-full bg-orange-600 hover:bg-orange-700 text-white" onClick={applyCustom}>
              Apply Custom Range
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

// ============================================================
// Reusable: MultiChip — popover with multi-select checkboxes
// ============================================================
const MultiChip = ({ label, options, selected, onChange, highlight, testId }) => {
  const [open, setOpen] = useState(false);
  const toggle = (key) => {
    onChange(selected.includes(key) ? selected.filter(k => k !== key) : [...selected, key]);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={testId}
          className={`inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
            highlight
              ? 'bg-orange-50 border-orange-300 text-orange-800 font-medium'
              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          {label}
          <ChevronDown size={12} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start">
        {options.length === 0 ? (
          <p className="text-xs text-gray-500 px-2 py-3 text-center">No options</p>
        ) : (
          options.map(opt => (
            <label
              key={opt.key}
              data-testid={`${testId}-opt-${opt.key}`}
              className="flex items-center gap-2 px-2.5 py-1.5 rounded hover:bg-orange-50 cursor-pointer text-sm text-gray-700"
            >
              <input
                type="checkbox"
                checked={selected.includes(opt.key)}
                onChange={() => toggle(opt.key)}
                className="accent-orange-600"
              />
              {opt.label}
            </label>
          ))
        )}
        {selected.length > 0 && (
          <button
            onClick={() => onChange([])}
            className="w-full text-center text-xs text-gray-500 hover:text-orange-700 py-1.5 border-t mt-1"
          >
            Clear
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
};

// ============================================================
// Right-side slide-in Filter Sheet
// ============================================================
const CollectionFiltersSheet = ({
  open, onClose, filters, setFilters, activeSection, setActiveSection,
  receivedByOptions, onClear,
}) => {
  const [draft, setDraft] = useState(filters);
  useEffect(() => { if (open) setDraft(filters); }, [open, filters]);

  const sectionsWithRuntime = FILTER_SECTIONS.map(s =>
    s.key === 'receivedBy' ? { ...s, options: receivedByOptions } : s
  );
  const section = sectionsWithRuntime.find(s => s.key === activeSection) || sectionsWithRuntime[0];

  const setDraftKey = (key, val) => setDraft(d => ({ ...d, [key]: val }));
  const apply = () => { setFilters(draft); onClose(); };
  const clearAndClose = () => { onClear(); onClose(); };

  // selected count per section for the rail
  const selectedCount = (key) => {
    const v = draft[key];
    if (Array.isArray(v)) return v.length;
    if (v && v.preset && v.preset !== 'all') return 1;
    return 0;
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col" data-testid="filters-sheet">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b">
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <ArrowLeft size={16} />
          </button>
          <h2 className="text-base font-semibold">Collection Filters</h2>
        </div>

        {/* Body: 2-pane (left rail + right content) */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left rail */}
          <div className="w-44 sm:w-52 border-r bg-gray-50 overflow-y-auto" data-testid="filter-rail">
            {sectionsWithRuntime.map(s => {
              const cnt = selectedCount(s.key);
              const active = activeSection === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => setActiveSection(s.key)}
                  data-testid={`filter-rail-${s.key}`}
                  className={`w-full text-left px-3 py-3 text-sm border-l-4 ${
                    active
                      ? 'bg-white border-orange-600 text-gray-900 font-medium'
                      : 'border-transparent text-gray-600 hover:bg-white'
                  }`}
                >
                  <div>{s.label}</div>
                  {cnt > 0 && <div className="text-[10px] text-orange-700 mt-0.5">{cnt} selected</div>}
                </button>
              );
            })}
          </div>

          {/* Right content */}
          <div className="flex-1 overflow-y-auto p-4" data-testid="filter-content">
            <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">{section.label}</p>

            {section.kind === 'date' ? (
              <DateSection value={draft[section.key]} onChange={(v) => setDraftKey(section.key, v)} />
            ) : (
              <MultiSection
                options={section.options}
                selected={draft[section.key]}
                onChange={(arr) => setDraftKey(section.key, arr)}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-3 flex gap-3">
          <Button variant="outline" className="flex-1" onClick={clearAndClose} data-testid="sheet-clear-all">
            Clear All
          </Button>
          <Button className="flex-1 bg-orange-600 hover:bg-orange-700 text-white" onClick={apply} data-testid="sheet-apply">
            Apply
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const DateSection = ({ value, onChange }) => {
  const presets = datePresets();
  return (
    <div className="space-y-1">
      {presets.map(p => (
        <label
          key={p.key}
          className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer hover:bg-orange-50 ${
            value.preset === p.key ? 'bg-orange-50' : ''
          }`}
        >
          <input
            type="checkbox"
            checked={value.preset === p.key}
            onChange={() => onChange({ preset: p.key, start: p.start, end: p.end })}
            className="accent-orange-600"
          />
          {p.label}
        </label>
      ))}
      {value.preset === 'custom' && (
        <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t">
          <input
            type="date"
            value={value.start}
            onChange={(e) => onChange({ ...value, start: e.target.value })}
            className="text-xs border border-gray-300 rounded px-2 py-1"
          />
          <input
            type="date"
            value={value.end}
            onChange={(e) => onChange({ ...value, end: e.target.value })}
            className="text-xs border border-gray-300 rounded px-2 py-1"
          />
        </div>
      )}
    </div>
  );
};

const MultiSection = ({ options, selected, onChange }) => {
  if (options.length === 0) return <p className="text-sm text-gray-400 italic">No options available.</p>;
  const toggle = (k) => onChange(selected.includes(k) ? selected.filter(x => x !== k) : [...selected, k]);
  return (
    <div className="space-y-1">
      {options.map(opt => (
        <label
          key={opt.key}
          className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer hover:bg-orange-50 ${
            selected.includes(opt.key) ? 'bg-orange-50' : ''
          }`}
        >
          <input
            type="checkbox"
            checked={selected.includes(opt.key)}
            onChange={() => toggle(opt.key)}
            className="accent-orange-600"
          />
          {opt.label}
        </label>
      ))}
    </div>
  );
};

export default Collections;
